import { useState, useEffect } from 'react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { DataTable } from '@/components/data-table/DataTable'
import { Shield, ShieldCheck, UserPlus, Trash2 } from 'lucide-react'
import { formatDate } from '@/lib/format'

export function SettingsPage() {
  const { user } = useAuth()
  const [mfaEnabled, setMfaEnabled] = useState(false)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [enrolling, setEnrolling] = useState(false)
  const [verifying, setVerifying] = useState(false)

  // User management
  const [users, setUsers] = useState<any[]>([])
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'employee'>('employee')
  const [inviting, setInviting] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    checkMfaStatus()
    checkAdminStatus()
  }, [user])

  useEffect(() => {
    if (isAdmin) {
      loadUsers()
    }
  }, [isAdmin])

  const checkMfaStatus = async () => {
    try {
      const { data } = await supabase.auth.mfa.listFactors()
      const verifiedFactor = data?.totp?.find(f => f.status === 'verified')
      setMfaEnabled(!!verifiedFactor)
    } catch (error) {
      console.error('Error checking MFA status:', error)
    }
  }

  const checkAdminStatus = async () => {
    if (!user) return
    try {
      const { data } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single()
      setIsAdmin((data as any)?.role === 'admin')
    } catch (error) {
      console.error('Error checking admin status:', error)
    }
  }

  const loadUsers = async () => {
    try {
      const { data } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false })
      setUsers(data || [])
    } catch (error) {
      console.error('Error loading users:', error)
    }
  }

  const handleEnrollMfa = async () => {
    setEnrolling(true)
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      })

      if (error) throw error

      setQrCode(data?.totp?.qr_code || null)
    } catch (error) {
      alert(`Error enrolling in MFA: ${error}`)
    } finally {
      setEnrolling(false)
    }
  }

  const handleVerifyMfa = async () => {
    setVerifying(true)
    try {
      const factors = await supabase.auth.mfa.listFactors()
      const factorId = factors.data?.totp?.[0]?.id

      if (!factorId) throw new Error('No factor found')

      const { data: challenge } = await supabase.auth.mfa.challenge({ factorId })
      if (!challenge) throw new Error('Failed to create challenge')

      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: verifyCode,
      })
      if (error) throw error

      alert('2FA enabled successfully!')
      setQrCode(null)
      setVerifyCode('')
      checkMfaStatus()
    } catch (error) {
      alert(`Error verifying MFA: ${error}`)
    } finally {
      setVerifying(false)
    }
  }

  const handleUnenrollMfa = async () => {
    if (!confirm('Are you sure you want to disable 2FA?')) return

    try {
      const factors = await supabase.auth.mfa.listFactors()
      const factorId = factors.data?.totp?.[0]?.id

      if (!factorId) throw new Error('No factor found')

      await supabase.auth.mfa.unenroll({ factorId })
      alert('2FA disabled successfully')
      checkMfaStatus()
    } catch (error) {
      alert(`Error disabling MFA: ${error}`)
    }
  }

  const handleInviteUser = async () => {
    if (!inviteEmail) {
      alert('Please enter an email address')
      return
    }

    setInviting(true)
    try {
      // In a real app, this would send an invitation email
      // For now, we'll just create a user record
      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: inviteEmail,
        email_confirm: true,
      })

      if (authError) throw authError

      const { error: userError } = await supabase
        .from('users')
        .insert({
          id: authData.user?.id,
          email: inviteEmail,
          role: inviteRole,
        } as any)

      if (userError) throw userError

      alert(`User invited: ${inviteEmail}`)
      setShowInviteModal(false)
      setInviteEmail('')
      setInviteRole('employee')
      loadUsers()
    } catch (error: any) {
      alert(`Error inviting user: ${error.message || error}`)
    } finally {
      setInviting(false)
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return

    try {
      const { error } = await supabase.auth.admin.deleteUser(userId)
      if (error) throw error

      alert('User deleted successfully')
      loadUsers()
    } catch (error) {
      alert(`Error deleting user: ${error}`)
    }
  }

  const userColumns = [
    { key: 'email', header: 'Email' },
    {
      key: 'role',
      header: 'Role',
      render: (u: any) => (
        <Badge variant={u.role === 'admin' ? 'info' : 'default'}>
          {u.role}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (u: any) => formatDate(u.created_at),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (u: any) => u.id !== user?.id ? (
        <Button
          size="sm"
          variant="danger"
          onClick={() => handleDeleteUser(u.id)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      ) : null,
    },
  ]

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'employee', label: 'Employee' },
  ]

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

      <div className="space-y-6">
        <Card>
          <h2 className="text-lg font-semibold mb-4">Profile</h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900">{user?.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">User ID</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">{user?.id}</dd>
            </div>
          </dl>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              {mfaEnabled ? (
                <ShieldCheck className="h-5 w-5 text-green-600 mr-2" />
              ) : (
                <Shield className="h-5 w-5 text-gray-400 mr-2" />
              )}
              <div>
                <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
                <p className="text-sm text-gray-600">
                  {mfaEnabled ? 'Your account is protected with 2FA' : 'Add an extra layer of security'}
                </p>
              </div>
            </div>
            {mfaEnabled ? (
              <Button variant="danger" onClick={handleUnenrollMfa}>
                Disable 2FA
              </Button>
            ) : (
              <Button onClick={handleEnrollMfa} loading={enrolling}>
                Enable 2FA
              </Button>
            )}
          </div>

          {qrCode && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold mb-3">Scan QR Code</h3>
              <div className="flex flex-col items-center space-y-4">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                <p className="text-sm text-gray-600 text-center">
                  Scan this QR code with your authenticator app, then enter the 6-digit code below
                </p>
                <div className="w-full max-w-xs space-y-3">
                  <Input
                    label="Verification Code"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                  />
                  <Button
                    onClick={handleVerifyMfa}
                    loading={verifying}
                    disabled={verifyCode.length !== 6}
                    className="w-full"
                  >
                    Verify and Enable
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {isAdmin && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">User Management</h2>
                <p className="text-sm text-gray-600">Manage system users and roles</p>
              </div>
              <Button onClick={() => setShowInviteModal(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Invite User
              </Button>
            </div>

            <DataTable
              data={users}
              columns={userColumns}
              emptyMessage="No users found"
            />
          </Card>
        )}
      </div>

      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite User"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Email Address"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="user@example.com"
            required
          />

          <Select
            label="Role"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as 'admin' | 'employee')}
            options={roleOptions}
            required
          />

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowInviteModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInviteUser}
              loading={inviting}
              disabled={!inviteEmail}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Send Invitation
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
